/**
 * Known Arch program ids → creature body + display label.
 * Unknown / high-volume unnamed programs keep DNA-derived species so shore
 * variety is preserved.
 */

import type { CreatureKey } from "../sprites/assets";
import { BODY_CREATURE_KEYS } from "../sprites/assets";
import { looksLikeHexId, toBase58Id } from "../util/base58";

export type KnownProgram = {
  /** Explorer / UI label. */
  label: string;
  /** Creature silhouette when this program is the primary callee. */
  body: CreatureKey;
};

/** Hex (lowercase) and base58 keys both accepted. */
const KNOWN: Record<string, KnownProgram> = {
  // System Program
  "0000000000000000000000000000000000000000000000000000000000000000": {
    label: "System Program",
    body: "tideling",
  },
  "11111111111111111111111111111111": {
    label: "System Program",
    body: "tideling",
  },
  // APL Token
  "06ddf6e1b9ea84412c10b8df021c100fc8871907c309c33535de209c341763bf": {
    label: "APL Token Program",
    body: "sparkwick",
  },
  TokenT4em53UrV4gSvZ3nCS2mZeHaqTLapwt6iZt6Mk: {
    label: "APL Token Program",
    body: "sparkwick",
  },
  // APL ATA
  "8c97231184927b77b5f180118fcc683414b77c521e5a77081cf71d5f606a5384": {
    label: "APL Associated Token Account",
    body: "mosscurl",
  },
  ATok9pxLsNzM5zJJ3UQpXBrMriHpZiY5Yio3GKYU4we3: {
    label: "APL Associated Token Account",
    body: "mosscurl",
  },
  // agent_launchpad
  "31b5d30f5a4391d78dc549651ded2476ca0a4ba06f54985cb1d151fa6be80716": {
    label: "agent_launchpad",
    body: "reefbit",
  },
  "4M3nTE8pK9DHa11D8KKdLLUuJCn6CGjtpmQR7Ba4ABJV": {
    label: "agent_launchpad",
    body: "reefbit",
  },
  // Arch Lend
  "53def2dc8516302842b10e356914d2a5f6b33425ba42aec684f706aa1cf64192": {
    label: "Arch Lend",
    body: "gloamink",
  },
  "6eQ1vLSAwmbT6SD3KQbNawAqis7LpzwpNTd7SJ1GU5cm": {
    label: "Arch Lend",
    body: "gloamink",
  },
  // Resharing
  "0650c38acc0212eb945bcfca0433cd8fb59b2e83d4e9644321ba9cf400000000": {
    label: "Resharing Program",
    body: "sparkwick",
  },
  Resharing1111111111111111111111111111111111: {
    label: "Resharing Program",
    body: "sparkwick",
  },
  // PropAMM
  "7a68831501d3a9806feff162e82815a36e1732964a2edd2b461faf69575c3628": {
    label: "PropAMM",
    body: "tideling",
  },
  "9EqAsENtgBA4Uo4wbS8LVdaQJjMKPpagMxgDVhxEWtKq": {
    label: "PropAMM",
    body: "tideling",
  },
  // BPF Loader
  "02c5b2d8e72d2ab2378b337749477d787ad013ef5e79e831e689448c00000000": {
    label: "BPF Loader",
    body: "sparkwick",
  },
  BpfLoader1111111111111111111111111111111111: {
    label: "BPF Loader",
    body: "sparkwick",
  },
  // ans_registry
  "3d9fbaa282268d8453a924692f254ad6c610668f36512db9fb50325ac2e4e079": {
    label: "ans_registry",
    body: "gloamink",
  },
  "59Z8pG1pDRsdADDscDF7rKbmi5GFP2nvFuPk8rrJHGLY": {
    label: "ans_registry",
    body: "gloamink",
  },
};

function normalizeProgramKey(id: string): string {
  const s = id.trim();
  if (looksLikeHexId(s)) return s.replace(/^0x/i, "").toLowerCase();
  return s;
}

/** First known program among ids, else null (keep DNA species). */
export function resolveKnownProgram(
  programIds: string[] | null | undefined,
): KnownProgram | null {
  if (!programIds?.length) return null;
  for (const id of programIds) {
    const hit = KNOWN[normalizeProgramKey(id)];
    if (hit) return hit;
  }
  return null;
}

/** BODY index for a creature key, or null if unknown. */
export function bodyIndexForCreature(key: CreatureKey): number | null {
  const i = BODY_CREATURE_KEYS.indexOf(key);
  return i >= 0 ? i : null;
}

/** Prefer base58 for display (explorer style). */
export function displayProgramId(id: string): string {
  return toBase58Id(id.trim());
}

/** Primary program id for the drawer (first id, display form). */
export function primaryProgramId(
  programIds: string[] | null | undefined,
): string | null {
  const first = programIds?.find((p) => p && String(p).trim());
  return first ? displayProgramId(first) : null;
}
